import { Color } from '@persona-id/design-tokens';
import { CircleCheckIcon, CircleXIcon } from '@persona-id/design-tokens/icons';
import { isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import { ReactNode } from 'react';
import { formatPhoneNumber } from 'react-phone-number-input';

import { MULTIPLE_CHECKS_STATUS } from 'dashboard/components/ChecksTable/ChecksTableRowMultipleChecks';
import {
  CheckMetadata,
  CheckMetadataItem,
  CheckMetadataItemType,
  renderCheckNameCell,
  renderMatchResultCell,
  renderMatchResultRowHeader,
  renderRequiredCell,
  renderStatusCell,
  TableRow,
} from 'dashboard/components/ChecksTable/computeCheckMetadata';
import Permissioned from 'dashboard/components/Permissioned';
import Pii from 'dashboard/components/Pii';
import { SsnMasker } from 'dashboard/components/SsnMasker';
import { ComparisonCheckPropertyType } from 'dashboard/components/VerificationDetail/VerificationTypes/DatabaseVerification/ComparisonChecksTable';
import { formatAddress, formatDate } from 'dashboard/lib/utils/formatters';
import { titleCase } from 'dashboard/lib/utils/stringFormat';
import {
  Check,
  CheckAttributeMatch,
  CheckName,
  CheckStatus,
  DatabaseAddressComparison,
  DatabaseBirthdateComparison,
  DatabaseIdentityComparison,
  DatabaseNameComparison,
  DatabasePhoneNumberComparison,
  DatabaseSocialSecurityNumberComparison,
  MatchGroupRequirementResult,
} from 'dashboard/models/check';
import { AttributeComparisonResultStatus } from 'dashboard/models/check-result/check-requirement';
import { VerificationDatabase } from 'dashboard/models/verification';
import Sticker from 'design-system/components/Sticker/Sticker';
import { CHECK_DISPLAY_CONFIG } from 'lib/constants/check';
import { Permission } from 'lib/permissions';
import { sortChecks } from 'lib/utils/checks';
import { VerificationStatus } from 'lib/verification';

const ATTRIBUTE_CHECK_NAMES = [
  CheckName.DatabaseAddressComparison,
  CheckName.DatabaseBirthdateComparison,
  CheckName.DatabaseIdentityComparison,
  CheckName.DatabaseNameComparison,
  CheckName.DatabasePhoneNumberComparison,
  CheckName.DatabaseSocialSecurityNumberComparison,
];

type DatabaseCheck =
  | DatabaseAddressComparison
  | DatabaseBirthdateComparison
  | DatabaseIdentityComparison
  | DatabaseNameComparison
  | DatabasePhoneNumberComparison
  | DatabaseSocialSecurityNumberComparison;

export const computeDatabaseAttributeChecksMetadata = (
  verification: VerificationDatabase,
): CheckMetadata => {
  const sortedChecks = sortChecks(verification.attributes?.checks || []);
  const isRedacted = verification.attributes?.['redacted-at'] != null;

  const identityComparisonCheck = sortedChecks.find(
    (check) => check.name === CheckName.DatabaseIdentityComparison,
  );

  if (identityComparisonCheck) {
    return computeMetadataForIdentityComparisonCheck(
      identityComparisonCheck as DatabaseIdentityComparison,
      verification,
      isRedacted,
    );
  }

  return computeMetadataForLegacyCheck(sortedChecks, verification, isRedacted);
};

function computeMetadataForIdentityComparisonCheck(
  identityComparisonCheck: DatabaseIdentityComparison,
  verification: VerificationDatabase,
  isRedacted: boolean,
): CheckMetadata {
  const usesMatchGroups = identityComparisonCheck.metadata?.['match-groups-result'] != null;

  if (usesMatchGroups) {
    const matchGroupsResults = identityComparisonCheck.metadata?.['match-groups-result'] || [];
    const matchGroupResult = matchGroupsResults[0];
    const matchRequirementResults = matchGroupResult?.['match-requirement-results'] || [];

    const comparisonMethods = matchRequirementResults
      .flatMap((result) => {
        // We may have no comparison results when they're from a match vendor
        return result['attribute-comparison-results']?.map(
          (comparison) => comparison['comparison-type'],
        );
      })
      ?.filter((value, index, self) => self.indexOf(value) === index)
      // Final filter to remove any undefined values
      ?.filter((value) => value);

    const rowsWithIndex: { [key: string]: TableRow[] } = {};
    matchGroupsResults.forEach((matchGroupResult, index) => {
      const matchRequirementResults = matchGroupResult?.['match-requirement-results'] || [];

      const rows = matchRequirementResults.map((result) => {
        const submitted = extractProperty(result) || '';
        const matchResult = result['best-result'] || result['match-level'];

        return [
          titleCase(result.description ?? result.name) || '',
          renderInputCell(submitted, result.name, isRedacted),
          renderMatchResultCell(
            matchResult as CheckAttributeMatch,
            result['attribute-comparison-results'],
          ) || '',
          renderStatusCell(result.status as AttributeComparisonResultStatus),
          renderRequiredCell(result.required),
        ];
      });

      // Note: Because this index is used in a Select component, it must be a string
      rowsWithIndex[`${index}`] = rows;
    });

    return {
      metadata: [
        {
          type: CheckMetadataItemType.DynamicTable,
          heading: {
            title: 'Identity Comparison Check',
          },
          options: matchGroupsResults.map((matchGroup, index) => ({
            value: `${index}`,
            label: (
              <>
                {matchGroup.status === CheckStatus.Passed ? (
                  <CircleCheckIcon size={14} color={Color.CoreGreen700} />
                ) : (
                  <CircleXIcon size={14} color={Color.CoreRed700} />
                )}
                {matchGroup.description || matchGroup.name}
                {index === 0 && verification.attributes.status === VerificationStatus.Passed && (
                  <Sticker>Best Match Group</Sticker>
                )}
                {index === 0 && verification.attributes.status !== VerificationStatus.Passed && (
                  <Sticker>Closest Match Group</Sticker>
                )}
              </>
            ),
          })),
          rowHeaders: [
            'Property',
            'Input',
            renderMatchResultRowHeader(comparisonMethods),
            'Status',
            'Required',
          ],
          rows: rowsWithIndex,
          tableEmptyState: 'No comparison checks were performed for this verification.',
        },
      ] as CheckMetadataItem[],
    };
  } else {
    const matchRequirementResults =
      identityComparisonCheck.metadata?.['match-requirement-results'] || [];
    const comparisonMethods = matchRequirementResults
      .flatMap((result) => result.comparisons.map((comparison) => comparison.type))
      .filter((value, index, self) => self.indexOf(value) === index)
      // Final filter to remove any undefined values
      .filter((value) => value);

    const rows = matchRequirementResults.map((result) => {
      const submitted = result['attribute-value'];

      return [
        titleCase(result.description ?? result.name) || '',
        renderInputCell(submitted, result['attribute-type'], isRedacted),
        renderMatchResultCell(result['best-result'] as CheckAttributeMatch, result.comparisons) ||
          '',
        renderStatusCell(result.status as CheckStatus),
        renderRequiredCell(result.required),
      ];
    });

    return {
      metadata: [
        {
          type: CheckMetadataItemType.Table,
          heading: {
            title: 'Identity Comparison Check',
          },
          rowHeaders: [
            'Property',
            'Input',
            renderMatchResultRowHeader(comparisonMethods),
            'Status',
            'Required',
          ],
          rows,
          tableEmptyState: 'No comparison checks were performed for this verification.',
        },
      ] as CheckMetadataItem[],
    };
  }
}

function computeMetadataForLegacyCheck(
  sortedChecks: Check[],
  verification: VerificationDatabase,
  isRedacted: boolean,
): CheckMetadata {
  const rows = sortedChecks
    .filter((check): check is DatabaseCheck => ATTRIBUTE_CHECK_NAMES.includes(check.name))
    .map((check) => {
      const checkDetails = CHECK_DISPLAY_CONFIG[check.name];
      if (checkDetails == null) {
        return null;
      }

      const checkMetadata = check.metadata;
      if (
        !checkMetadata ||
        /** @ts-expect-error: TODO: Fix this to use type comparison instead of checking for null */
        checkMetadata['match-level-minimum'] == null ||
        /** @ts-expect-error: TODO: Fix this to use type comparison instead of checking for null */
        checkMetadata['match-result'] == null
      ) {
        return null;
      }

      /** @ts-expect-error: TODO: This should be fixed when the above is fixed */
      const matchResult = checkMetadata['match-result'] as CheckAttributeMatch;

      const {
        'name-first': nameFirst,
        'name-middle': nameMiddle,
        'name-last': nameLast,
        birthdate,
        'address-street-1': addressStreet1,
        'address-street-2': addressStreet2,
        'address-city': addressCity,
        'address-subdivision': addressSubdivision,
        'address-postal-code': addressPostalCode,
        'phone-number': phoneNumber,
        'identification-number': identificationNumber,
      } = verification.attributes;

      let submitted: string | ReactNode | undefined;
      let type: ComparisonCheckPropertyType | undefined;
      switch (check.name) {
        case CheckName.DatabaseAddressComparison:
          submitted = formatAddress({
            addressStreet1,
            addressStreet2,
            addressCity,
            addressSubdivision,
            addressPostalCode,
            color: 'primary',
          });
          type = 'address';
          break;
        case CheckName.DatabaseBirthdateComparison:
          submitted = birthdate;
          type = 'date';
          break;
        case CheckName.DatabaseNameComparison:
          submitted = [nameFirst, nameMiddle, nameLast].filter(Boolean).join(' ');
          break;
        case CheckName.DatabasePhoneNumberComparison:
          submitted = phoneNumber;
          type = 'phone_number';
          break;
        case CheckName.DatabaseSocialSecurityNumberComparison:
          submitted = identificationNumber;
          type = 'social_security_number';
          break;
      }

      return [
        renderCheckNameCell(checkDetails.name, checkDetails.description),
        renderInputCell(submitted || '', type, isRedacted),
        /** @ts-expect-error: TODO: This should be fixed when the above is fixed */
        renderMatchResultCell(matchResult, undefined, checkMetadata['match-level-minimum']),
        renderStatusCell(check.status),
      ];
    })
    .filter(Boolean);

  return {
    metadata: [
      {
        type: CheckMetadataItemType.Table,
        heading: {
          title: 'Comparison Checks',
        },
        rowHeaders: ['Property', 'Input', 'Match Result', 'Status'],
        rows,
        tableEmptyState: 'No comparison checks were performed for this verification.',
      },
    ] as CheckMetadataItem[],
  };
}

function extractProperty(matchResult: MatchGroupRequirementResult) {
  // Not all match results will normalize an input attribute. In the event that they don't,
  // we fall back to the submitted attribute.
  if (matchResult['normalized-claimed-attributes']) {
    return matchResult['normalized-claimed-attributes'][0];
  }

  if (matchResult['claimed-attributes']) {
    // Handle the case that we don't have an attribute value
    if (matchResult['claimed-attributes'][0] == null) {
      return null;
    }
    // In the event that we've returned an object, we'll want to extract the first value
    if (typeof matchResult['claimed-attributes'][0] === 'object') {
      const values = Object.values(matchResult['claimed-attributes'][0]);
      return values.length > 0 ? values[0] : '';
    }
    return matchResult['claimed-attributes'][0];
  }
  return '';
}

function renderInputCell(
  value: string | ReactNode,
  propertyType: ComparisonCheckPropertyType | string | undefined,
  isRedacted: boolean,
) {
  let formattedValue = value;

  // Note: `value` can be a ReactNode when it's an address and we have used `formatAddress`
  if (typeof value === 'string') {
    switch (propertyType) {
      case 'date':
      case 'birthdate':
        formattedValue = `${formatDate(value)} (${Math.floor(
          DateTime.local().diff(DateTime.fromISO(value), 'years').years,
        )} years old)`;
        break;
      case 'phone_number': {
        const formattedPhoneNumber = formatPhoneNumber(value, 'National');
        formattedValue = isEmpty(formattedPhoneNumber) ? value : formattedPhoneNumber;
        break;
      }
      case 'social_security_number': {
        // TODO: we should handle truncating the SSN on the backend when a user doesn't have perms to view the full SSN
        if (!value) {
          return null;
        }
        return (
          <Permissioned
            permission={Permission.VerificationDatabaseSsnView}
            fallback={<SsnMasker ssn={value.substring(value.length - 4)} />}
          >
            <SsnMasker ssn={value} />
          </Permissioned>
        );
      }
    }
  }

  return <Pii isRedacted={isRedacted}>{formattedValue}</Pii>;
}

export function getStatusForCheckRow(verification: VerificationDatabase) {
  const sortedChecks = sortChecks(verification.attributes?.checks || []);

  const identityComparisonCheck = sortedChecks.find(
    (check) => check.name === CheckName.DatabaseIdentityComparison,
  );

  if (identityComparisonCheck) {
    return identityComparisonCheck.status;
  }

  return MULTIPLE_CHECKS_STATUS;
}
